/**
 * ScreenerView — fetches one section's screen and hands it to the renderer.
 *
 * The analogue of `ChartGroupView`: this component owns the request lifecycle
 * and hands decoded frames to the renderer. `@qufolio/screener-grid` fetches
 * nothing (it has to stay WebView-hostable, per D18), and the transport never
 * imports AG Grid — this is the seam where they meet.
 *
 * **A screen is a group, or it is nothing.** `codes` — what the bar has picked
 * — is the only universe this view will screen. There is no second source: the
 * `ws://…/ws/scr` finset socket that used to run whenever nothing was picked is
 * gone, and with it the `ScreenerConfig.group`/`screen` pair it was named by.
 * That pair is whatever the workspace was saved with, on this machine
 * `SP500/JASDOTS` — a screen retired years ago — so the state nobody chooses
 * loaded a screen nobody asked for and reported it as if it had been asked for.
 * Nothing picked now renders "No group picked" and stops.
 *
 * Grid state changes come back as contract-shaped `ScreenerConfig` patches and
 * are dispatched into the workspace, so the workspace stays the source of truth
 * and the same state replays on any renderer.
 *
 * ## A screen arrives in waves, and this is where they are folded together
 *
 * A screen is not one answer. Wave 1 — the roster, the price row, the
 * descriptive columns — comes straight from `qf-us` and refreshes on its own
 * cadence; the computed columns are pushed separately whenever whoever computes
 * them has an answer. **The two are not synchronised**, and the rule that makes
 * that survivable is one line: *a push that does not carry a column must not
 * clear that column.* `applyScreenerPush` in the island enforces it, this view
 * feeds it, and `docs/plans/screener-fast-path.md` is why.
 *
 * Two consequences worth knowing before changing anything here. A computed
 * column can be up to one compute cycle behind the price beside it, and **that
 * skew is accepted** — do not blank the column on each tick to hide it. And a
 * computed column with nobody computing it renders *unavailable*, not pending:
 * until a compute service exists that is every history-needing column, and
 * saying so is the point rather than a gap.
 *
 * Where the waves come from is configuration. `VITE_QV_SCREEN_URL` set ⇒ a
 * subscription (`api/screen-stream.ts`), which is what this is for; unset ⇒ the
 * single `GET` this view has always made, which is one wave carrying everything.
 * Both land in `applySnapshot`, so neither path has a merge rule of its own.
 *
 * This is also where the island gets its colors. It has no theme of its own (it
 * must stay WebView-hostable, D18), so the host injects a resolver bound to the
 * workspace's theme — the *same* theme the chart resolves against. This view
 * used to carry its own two-entry `{ "chart.up": "#22c55e" }` map, which is how
 * a green screener cell and a green up candle ended up different greens.
 *
 * ## The bar owns the controls, and this owns their surfaces
 *
 * Columns and the filter toggle live in `ScreenerBar`, but the *pickers*
 * they open render here, over the whole panel. Two reasons, and the second is
 * the load-bearing one: the bar sets `overflow: hidden` so its chip measurement
 * works, and a popover inside it would be clipped; and `ColumnPicker` positions
 * itself against its nearest positioned ancestor, which needs to be something
 * the size of the panel rather than the size of a 34px row.
 */

import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import { mintUlid, type SavedScreenerFilter, type ScreenerConfig } from "@qufolio/model";
import {
  type ConnectionStatus,
  type ScreenerColumnMeta,
  type ScreenerField,
  type ScreenerFrame,
  type ScreenerRosterSnapshot,
  type TransportError,
} from "@qufolio/transport";
import {
  agThemeClass,
  applyScreenerPush,
  buildUniverse,
  categoryLookup,
  ColumnPicker,
  mergedMeta,
  readColumnSources,
  readScreenerPush,
  ScreenerPanel,
  sealedConditions,
  applySelection,
  selectedFilterIds,
  serverComputedColumns,
  serverConditionColumns,
  withSealedConditions,
  matchesFilter,
  readScreenerTimings,
  type AddRowBinding,
  type ScreenerConfigPatch,
  type NewsWindow,
  type ScreenerTimings,
  type TrackListBinding,
  type TrackMark,
  type ScreenerExporter,
  plainText,
} from "@qufolio/screener-grid";
import { liveWindow, readMarks, subscribeMarks } from "../lib/screener-marks";
import { liveStoredFields } from "../lib/live-stored";
import { useTheme, useTokenResolver } from "./theme";
import { ScreenerBar, type ListTools, type ScreenerBarProps } from "./ScreenerBar";
import { asWorkingSet, recallCurrent, recallRecent, rememberCurrent, rememberRecent } from "../lib/screener-bar";
import type { ScreenerPlacement } from "../lib/layout-templates";
import { NewListDialog } from "./NewListDialog";
import { ScreenerExportDialog } from "./ScreenerExportDialog";
import { TrackListImportDialog } from "./TrackListImportDialog";
import { csvCell } from "../lib/screener-export";
import { defaultScreen, isAuthored, useScreens } from "../lib/screens";
import { applyScreenTo, definitionOf, sameDefinition, useScreenSync } from "../lib/screen-sync";
import {
  discardAuthored,
  forgetBaseline,
  loadAuthored,
  markBaseline,
  publishAuthored,
  screenLoader,
  useDraftMeta,
  useDraftSync,
} from "../lib/screen-draft";
import { useScreenLinks } from "../lib/screen-links";
import { DraftBar, DraftUnreadBar } from "./DraftBar";
import {
  panelOf,
  splitOverlay,
  useOverlaySync,
  type Overlay,
  type ScreenBase,
} from "../lib/screen-overlay";
import { fetchOverlay, fetchPreset, saveOverlay, saveScreen } from "../api/presets";
import { TrackListMenu } from "./TrackListMenu";
import { available } from "../lib/features";
import {
  addSymbols,
  colourToken,
  createList,
  defaultList,
  listForKey,
  listsFor,
  removeFromAll,
  toggleSymbols,
  trackListsSnapshot,
  useTrackLists,
  type TrackList,
} from "../lib/track-lists";
import { addFromRow, addRowSuggestions, listForScreen } from "../lib/track-list-add";
import { createListInChunks, seedNewList } from "../lib/track-list-import";
import { publishScreenerCommands } from "../lib/screener-commands";
import { loadSymbolIndex, symbolIndex, useSymbolIndex } from "../lib/symbol-index";
import {
  recallSelection,
  rememberSelection,
  seedSelection,
  selectedCodes,
  DEFAULT_SELECTION,
  type CombineMode,
  type GroupSelection,
} from "../lib/group-selection";
import {
  fetchGroupTypes,
  fetchGroups,
  fetchGroupsScreen,
  fetchStrategyHistory,
  fetchStrategyWhy,
  symbolsConfigured,
  type StrategyHistory,
  type StrategyWhy,
  type SymbolGroup,
  type SymbolGroupType,
} from "../api/symbols";

import { screenStreamConfigured, useScreenStream } from "../api/screen-stream";
import { scanConfigured, useScanColumns } from "../api/scan-columns";
import { recsAsk, recsKey, withoutRecs, withRecsDirection } from "../lib/recs";
import { NewsListPopover } from "./NewsListPopover";
import {
  directionField,
  directionSort,
  directionTitle,
  nextDirection,
  withDirection,
} from "../lib/screener-direction";
import {
  MEMBER_FIELDS,
  registerScreenIndex,
  screenIndexWeighting,
  setScreenIndexWeighting,
  withTicks,
  type MemberRow,
} from "../lib/topdown";
import { RecsBar } from "./RecsBar";
import {
  heldMonth,
  publishStrategyOnScreen,
  shiftMonth,
  strategyCode,
  strategyColumns,
  withEntryText,
  withoutStratColumns,
} from "../lib/strategy";
import { StrategyHistoryStrip, StrategyStepper } from "./StrategyControls";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { useEntitlements } from "../lib/entitlements";
import { marketByCode } from "../lib/market";
import { isPdField } from "../lib/pd-metrics";
import { TABLET_QUERY, useIsNarrow, useMediaQuery } from "../lib/use-narrow";
import { ScreenerTouchBars } from "./ScreenerTouchBars";
import { useSparkHover } from "./SparkCard";
import { LeadersPanel, type LeadersSettings } from "./LeadersPanel";
import { fetchLeaders, leadersConfigured, type LeadersAnswer } from "../api/leaders";
import {
  applyPanel,
  DEFAULT_THRESHOLD,
  FIELDS as LEADER_FIELDS,
  isPanelField,
  leadersOf,
  withPanelColumns,
} from "../lib/leaders-panel";

/**
 * The frames rendered when no group is picked: none.
 *
 * Module-level so it is the *same* empty array on every render — `activeFrames`
 * is a memo dependency, and a fresh `[]` each time would rebuild the column
 * universe on every keystroke elsewhere in the app.
 */
const NO_FRAMES: readonly ScreenerFrame[] = [];

/**
 * How long a changed selection waits before it is fetched.
 *
 * Multi Select made this necessary. Its tick-list stays open while you pick, so
 * choosing five groups is five selections in a couple of seconds — and each one
 * was a full screen build. That is wasteful on any server and *rationed* on this
 * one: `/sym/screen` and `/sym/groups/{code}/screen` both spend from qv-data's
 * `HEAVY` budget, 300 an hour per caller, so a few minutes of browsing could
 * plausibly exhaust an afternoon's allowance and start answering 429.
 *
 * Short enough to feel immediate against a screen build that costs seconds when
 * the server's frame is cold, long enough that a run of ticks collapses to one
 * request. The debounce is the effect's own cleanup: each change cancels the
 * pending timer, which is what React's teardown already does for free.
 *
 * Deliberately **not** applied to Refresh or to the first load — see
 * `fetchGroupsScreen`'s effect. Both are single deliberate events with nothing
 * to coalesce, and delaying either only makes the app feel slow.
 *
 * `SUBSCRIBE_DEBOUNCE_MS` in `api/screen-stream.ts` is this number and this
 * reason applied to the subscription. Two constants rather than one because the
 * two are justified separately — a subscribe is not rationed the way a `HEAVY`
 * request is — but they must not drift apart without somebody deciding to.
 */
const SELECTION_DEBOUNCE_MS = 300;

export interface ScreenerViewProps {
  config: ScreenerConfig;
  onConfigChange?: (patch: ScreenerConfigPatch) => void;
  /** The saved-filter library changed — a library edit, not a section patch. */
  onSavedFiltersChange?: (filters: SavedScreenerFilter[]) => void;
  /**
   * The decoded result schema, published as it arrives.
   *
   * The fetch lives here, but a *docked* editor is a sibling in the layout
   * rather than a child — so the schema has to travel up. Without it the editor
   * would have no columns to offer and no types to infer.
   */
  onSchema?: (schema: {
    fields: ScreenerField[];
    meta: ScreenerColumnMeta[];
    /**
     * What the last response said it cost, per stage.
     *
     * Rides with the schema rather than on a callback of its own because it
     * arrives on the same response and has the same lifetime — one more field
     * on the thing the host already stores per screen.
     */
    timings?: ScreenerTimings;
  }) => void;
  /** A row was picked — the symbol to chart. */
  onSelectSymbol?: (symbol: string) => void;
  /**
   * Hide the whole screener — drawn as the × at the end of the group bar.
   *
   * Passed straight through: whether the screener is on screen is the shell's
   * view state (D8), not something this view or its config knows about.
   */
  onHide?: () => void;
  /**
   * Render the rows some other way, in place of the AG Grid panel.
   *
   * The mobile shell's List tab. Everything above the rows — the group bar, the
   * fetch, the selection, the filters, the column config — is unchanged and
   * still owned here; only the thing that draws them differs. A second view
   * that fetched its own screen would double every request and let the two
   * disagree about which group is showing.
   *
   * Supplied ⇒ `hideColumns`-style parity is the caller's problem: the bar's own
   * Columns and Filters buttons still work, because they act on `config`.
   */
  renderRows?: (rows: MobileRowsInput) => ReactNode;
  /**
   * This view is a TopDown panel's Symbols grid (`docs/features/topdown.md`).
   * The ticks are ANDed into the filter on the way to the grid, and
   * `onMembers` hears the rows the base screen's own filter keeps — before
   * the ticks — which is what every level is built from.
   */
  topDown?: {
    ticks: { sector: string[]; industry: string[] };
    onMembers: (rows: MemberRow[]) => void;
    /** The screen a ticked sealed condition is asked for under — the base, which owns the library. */
    conditionsScreen?: string;
  };
  /**
   * The panel whose screen the bar shows and switches, when it is not this
   * view's own config — a TopDown's Symbols grid draws the *base* screen, but
   * its bar names and switches the TopDown panel (`TopDownView`).
   */
  screenHost?: { config: ScreenerConfig; onConfigChange?: (patch: ScreenerConfigPatch) => void };
  /** Extra screen settings for the bar's slot. Recs' own win. */
  settings?: ReactNode;
  /** A screen's own choices under the bar's ⋯ (a TopDown's layout). */
  moreSection?: ScreenerBarProps["moreSection"];
  /**
   * Lays the grid out with what belongs beside it, under the bar — a TopDown's
   * Sector and Industry grids. Absent, the grid fills the panel.
   */
  frameGrid?: (grid: ReactNode) => ReactNode;
  /** ＋ New in the bar's screen list — `App`'s, the editor strip's own add. */
  onNewScreen?: () => void;
  /** ✎ Edit this screen in the bar's screen list — opens the docked editor. */
  onEditScreen?: (via?: "columns") => void;
  /**
   * Which side the screener is docked on (`screenerPlacement`). Left or right
   * stacks the bar into rows; see `isTall` in `lib/screener-bar.ts`.
   */
  dockSide?: ScreenerPlacement;
}

/** What a custom row renderer is given. The frames are already decoded. */
export interface MobileRowsInput {
  frames: readonly ScreenerFrame[];
  config: ScreenerConfig;
  meta: readonly ScreenerColumnMeta[];
  onSelectSymbol?: (symbol: string) => void;
  /** The touch screener's search: symbol or name, view state only. */
  query: string;
  /** What the conditions kept, of how many — for the top row's counts. */
  onRowCounts: (matched: number, total: number) => void;
}

/**
 * The screener-grid island deliberately has no id dependency — it must stay
 * WebView-hostable (D18) — so the app supplies the minter. `mintUlid` is the
 * contract's own, which is what keeps a saved filter's id schema-valid.
 */
const mintId = () => mintUlid();

export function ScreenerView({
  config: stored,
  onSelectSymbol,
  onConfigChange,
  onSavedFiltersChange,
  onSchema,
  onHide,
  renderRows,
  topDown,
  screenHost,
  settings: extraSettings,
  moreSection,
  frameGrid,
  onNewScreen,
  onEditScreen,
  dockSide,
}: ScreenerViewProps) {
  const theme = useTheme();
  // Hover a symbol → its last 60 bars (SparkCard). No hover on a phone.
  const narrow = useIsNarrow();
  /**
   * The touch screener's search box (`ScreenerTouchBars`). View state: never in
   * the config, and cleared with the universe, as the grid's own search is.
   */
  const [touchQuery, setTouchQuery] = useState("");
  /** A phone on its side: the touch controls become a rail on the right. */
  const landscape = useMediaQuery("(orientation: landscape)");
  /** An iPad: the touch screener with its room used (labels, inline counts, centred sheets). */
  const tablet = useMediaQuery(TABLET_QUERY);
  const spark = useSparkHover();

  // ── header marks (docs/plans/screener-bands.md) ──────────────────────────
  // The yellow/blue lines show while the market is live; re-asked every 30 s,
  // so crossing the open or the close repaints within one tick.
  const marks = useSyncExternalStore(subscribeMarks, readMarks);
  const extendedHours = stored.extendedHours === true;
  const [live, setLive] = useState(() => liveWindow(new Date(), extendedHours, marks.holidays));
  useEffect(() => {
    const check = () => setLive(liveWindow(new Date(), extendedHours, marks.holidays));
    check();
    const id = window.setInterval(check, 30_000);
    return () => window.clearInterval(id);
  }, [extendedHours, marks.holidays]);
  const categoryOf = useMemo(() => categoryLookup(marks.catalog), [marks.catalog]);
  const resolveToken = useTokenResolver();
  const [status, setStatus] = useState<string>("no group");
  const [error, setError] = useState<string | null>(null);
  /** How many rows survived the filter, reported by the grid. */
  const [matchedRows, setMatchedRows] = useState<number | null>(null);

  /**
   * Group mode — the symbol universe picked in the bar above.
   *
   * Held here rather than in `config` on purpose. Which universe you are
   * *browsing* is a view decision, and writing it to the workspace on every
   * click would put a save behind each chip. When a group should become the
   * section's saved universe, that is a deliberate action, not a side effect of
   * looking. (Same reasoning as the docked editor's ephemeral column, D8.)
   */
  const [types, setTypes] = useState<SymbolGroupType[]>([]);
  const [allGroups, setAllGroups] = useState<SymbolGroup[]>([]);
  const [selection, setSelection] = useState<GroupSelection>(() => {
    // Both bars read both modes: Single came back to the desktop ◎ menu
    // (owner, 2026-10-27, reversing the screener-bar plan's decision 2).
    return recallSelection(stored.id) ?? DEFAULT_SELECTION;
  });
  const [groupFrames, setGroupFrames] = useState<readonly ScreenerFrame[] | null>(null);
  const [groupMeta, setGroupMeta] = useState<ScreenerColumnMeta[]>([]);

  /**
   * **Two configs, and which one each line reads is the point.** `stored` is the
   * section as saved — a linked column or condition is its link and the
   * member's view (`docs/plans/screen-links.md`). `config` is that with every
   * link resolved from its source screen: what the grid draws, what the server
   * is asked to compute, which conditions are sent. Everything that *writes* —
   * the screen sync, the layer, the draft, a refill's comparison — reads
   * `stored`, so a source's format is never written into a member's copy; a
   * patch the grid sends back is reduced to links by `updateScreener`.
   */
  const { screens } = useScreens();
  const { can, me } = useEntitlements();
  const publishTo = me?.publishTo ?? null;
  const linkLoad = useMemo(() => screenLoader(publishTo), [publishTo]);
  const labelOf = useCallback(
    (field: string) => groupMeta.find((m) => m.field === field)?.label || undefined,
    [groupMeta],
  );
  const { config, links: screenLinks } = useScreenLinks<ScreenerConfig>({
    config: stored,
    screens,
    load: linkLoad,
    labelOf,
  });
  /**
   * The panel's Recs setting, **only while the screen it shows is a Recs
   * screen**. A panel is copied about — a group's layout publishes its author's
   * panel to every member — and a member who cannot see the screen it named
   * falls back to another one (Persons) still holding the setting, which drew
   * D W M · Long Short over an ordinary screen (owner, 2026-10-16). The screen
   * decides, not the copy.
   */
  const recsInForce = useMemo(() => {
    if (!config.recs || !config.screenRef) return undefined;
    return screens.find((s) => s.key === config.screenRef)?.recs ? config.recs : undefined;
  }, [config.recs, config.screenRef, screens]);

  /**
   * What the next payload merges *into* — not the same thing as what is on
   * screen, deliberately.
   *
   * A ref because the merge has to happen outside a state updater: the merged
   * schema is what `onSchema` publishes to the docked editor, and reading a
   * result back out of `setState` is not something React offers. It is also the
   * one place the merge baseline can be *dropped* without blanking the grid —
   * see the reset effect below.
   */
  const mergeBase = useRef<readonly ScreenerFrame[] | null>(null);

  /**
   * A wave-2 push that arrived before wave 1 had delivered a roster.
   *
   * **Held, not applied and not dropped.** Wave 2 has no roster and no authority
   * over the row set, so there is nothing for it to merge into yet; applying it
   * anyway is what put a finished-looking, empty screen in front of the user on
   * 2026-09-11. Dropping it is the other wrong answer — the tier pushes from
   * memory the moment a client connects and then only once a tick, so the
   * computed columns would be blank for a whole tick on every first paint, which
   * is the property the socket exists to protect.
   */
  const pendingScan = useRef<readonly ScreenerFrame[] | null>(null);

  /**
   * Whether the selected saved filters are applied — view state, not a patch.
   *
   * Turning the filter off must not rewrite the workspace: a config patch would
   * put a save behind every click, and clearing `activeFilterRefs` would make
   * the saved-filter picker forget what was ticked, so turning it back on could
   * not restore the same selection. Instead the *effective* config handed to
   * the panel drops `filter`, exactly as `rollupConfig` already does for the
   * rollup tabs — the workspace is untouched and a reload comes back on.
   */
  const [filtersEnabled, setFiltersEnabled] = useState(true);
  const [columnsOpen, setColumnsOpen] = useState(false);

  /**
   * The Refresh button — a nonce, so asking twice means twice (D8's transient
   * rule, the same shape as the chart's jump-to-date).
   *
   * `seenRefresh` is what makes it a *refresh* rather than a dependency: the
   * fetch effect also re-runs when the group or a computed column changes, and
   * those must not tell the server to drop its cache — that is a ~5.4s frame
   * rebuild, and clicking a different group would pay it. Only the run where
   * the nonce actually moved forces one.
   */
  const [refreshNonce, setRefreshNonce] = useState(0);
  const seenRefresh = useRef(0);
  const [refreshing, setRefreshing] = useState(false);
  /**
   * Whether a group screen has ever arrived for this section.
   *
   * The one input to the debounce that is not about the selection: the first
   * fetch has nothing queued behind it to coalesce with, so making it wait only
   * delays first paint. A ref rather than state because nothing renders from
   * it, and setting it must not itself cause a render inside the `.then`.
   */
  const fetched = useRef(false);

  const handleRefresh = useCallback(() => {
    setRefreshing(true);
    setRefreshNonce((n) => n + 1);
  }, []);

  const localeForMarket = (code: string) => marketByCode(code).locale;

  /** Groups by type, which is what Multi Select needs and Single Select slices. */
  const groupsByType = useMemo(() => {
    const byType = new Map<string, SymbolGroup[]>();
    for (const g of allGroups) {
      const list = byType.get(g.groupType);
      if (list) list.push(g);
      else byType.set(g.groupType, [g]);
    }
    return byType as ReadonlyMap<string, readonly SymbolGroup[]>;
  }, [allGroups]);

  /** The universe the bar is asking for. Empty ⇒ nothing is screened. */
  const codes = useMemo(() => selectedCodes(selection), [selection]);
  const codesKey = useMemo(
    () => `${selection.combine}:${codes.join(",")}`,
    [selection.combine, codes],
  );
  // ── Strat: one strategy chip is the screen (docs/plans/strategy-lists.md, phase 5) ──
  // A chip opens on the **held** month — what you own today — not the newest,
  // which is usually next month's preview (owner, 2026-10-27). `stratMonth`
  // undefined is the newest month, which follows the next build.
  const stratCode = useMemo(() => strategyCode(codes), [codes]);
  const [stratMonth, setStratMonth] = useState<string | undefined>(undefined);
  const [stratWhy, setStratWhy] = useState<StrategyWhy | null>(null);
  const [stratNewest, setStratNewest] = useState<string | null>(null);
  const [stratHistory, setStratHistory] = useState<StrategyHistory | null>(null);
  useEffect(() => {
    setStratMonth(stratCode ? heldMonth() : undefined);
    setStratNewest(null);
    setStratHistory(null);
    if (!stratCode) return;
    let live = true;
    void fetchStrategyHistory(stratCode, 12).then((h) => {
      if (!live) return;
      setStratHistory(h ?? null);
      const newest = h?.months[h.months.length - 1];
      if (newest) setStratNewest(newest.slice(0, 7));
    });
    return () => {
      live = false;
    };
  }, [stratCode]);
  // The month on screen and its status — the stepper's label and badge, and
  // the newest month the forward step stops at. Re-asked on Refresh.
  useEffect(() => {
    setStratWhy(null);
    if (!stratCode) return;
    let live = true;
    void fetchStrategyWhy(stratCode, stratMonth).then((why) => {
      if (!live) return;
      setStratWhy(why ?? null);
      if (why && stratMonth === undefined) setStratNewest(why.month.slice(0, 7));
      // Nothing for the held month (a strategy whose first month is still a
      // preview): fall back to the newest rather than an empty grid.
      if (!why && stratMonth !== undefined && stratMonth === heldMonth()) setStratMonth(undefined);
    });
    return () => {
      live = false;
    };
  }, [stratCode, stratMonth, refreshNonce]);
  const stepStrat = useCallback(
    (by: -1 | 1) => {
      const from = stratMonth ?? stratNewest;
      if (!from) return;
      const next = shiftMonth(from, by);
      setStratMonth(stratNewest && next >= stratNewest ? undefined : next);
    },
    [stratMonth, stratNewest],
  );
  const pickStrat = useCallback(
    (month: string) => setStratMonth(stratNewest && month >= stratNewest ? undefined : month),
    [stratNewest],
  );
  const stratKey = stratCode ? `|m:${stratMonth ?? ""}` : "";
  // The docked editor shows the Strat list while one is on screen.
  useEffect(() => {
    publishStrategyOnScreen(stratCode);
    return () => publishStrategyOnScreen(null);
  }, [stratCode]);

  // A search typed against one universe must not silently thin out the next —
  // the grid's own search follows the same `searchScope`.
  useEffect(() => setTouchQuery(""), [codesKey, config.screenRef]);

  /**
   * The locale the grid's `compact` columns format in — the **screened
   * market's**, not the reader's.
   *
   * Read off the selected groups rather than stored anywhere: a screener shows
   * one market at a time and the group is what says which (D40), so a separate
   * setting could only ever disagree with it. Falls back to the browser's when
   * nothing is selected yet — which renders no rows anyway — and for a
   * multi-select spanning two markets.
   */
  const screenLocale = useMemo(() => {
    const selected = allGroups.filter((g) => codes.includes(g.code));
    const markets = new Set(selected.map((g) => g.market).filter(Boolean));
    // Exactly one market is the only case with an unambiguous answer. A
    // multi-select spanning two would have no coherent column to format anyway,
    // so leaving it to the browser is honest rather than arbitrary.
    return markets.size === 1 ? localeForMarket([...markets][0]!) : undefined;
  }, [allGroups, codes]);

  /**
   * The computed columns the server has to produce — the ones whose expressions
   * read bar history and so cannot be evaluated from a single row.
   *
   * Serialised into the effect's dependency list rather than passed by identity:
   * `config.columns` is a fresh array on every patch, so keying on the object
   * would reconnect the socket on an unrelated column resize. Keying on the
   * *content* reconnects exactly when the server's answer would differ — which
   * it must, because these columns are part of the server's channel identity.
   */
  const serverComputed = useMemo(
    // Plus the applied filter's history-needing ScanScript conditions, as
    // hidden fields — a condition written directly needs no column of its own
    // (docs/plans/pattern-screener.md).
    () => [...serverComputedColumns(config.columns), ...serverConditionColumns(config.filter)],
    [config.columns, config.filter],
  );
  const computedKey = useMemo(() => JSON.stringify(serverComputed), [serverComputed]);

  /**
   * The ticked sealed conditions — a group's rules this caller never holds,
   * asked for by `{field, screen, id}` (`docs/plans/screen-links.md`). All of
   * them go to wave 1 beside `computed`, and part of the same key: ticking one
   * is a different question, so it resubscribes and drops the merge baseline.
   * Wave 2 gets only the ones wave 1 hands over — see `sealedLive`.
   */
  // A linked condition goes by its link; one that resolved to nothing is left
  // out rather than holding every row back (`sealedConditions`).
  // A TopDown's Symbols grid has no `screenRef` of its own; its conditions are
  // the base screen's, so a sealed one is asked for under that screen.
  const conditionsScreen = topDown?.conditionsScreen;
  const sealed = useMemo(
    () =>
      sealedConditions(
        conditionsScreen ? { ...config, screenRef: conditionsScreen } : config,
        screenLinks.missing,
      ),
    [config, screenLinks.missing, conditionsScreen],
  );
  const sealedKey = useMemo(() => (sealed.length > 0 ? JSON.stringify(sealed) : ""), [sealed]);

  /** The picked group's frames, or none — there is no other source. */
  const activeFrames = groupFrames ?? NO_FRAMES;

  // ── delivery: one payload in, folded into what is on screen ────────────────

  /** The dictionary last delivered, so a wave that carries none cannot erase it. */
  const heldMeta = useRef<ScreenerColumnMeta[]>([]);

  /**
   * One arriving payload, folded in — the single entry point for both delivery
   * paths.
   *
   * A subscription push and the `GET` response are the *same named frames*, so
   * they must not have two merge rules: `applyScreenerPush` reads the payload's
   * own `push` frame to learn which columns it speaks for and leaves every other
   * one exactly as it was. A payload with no `push` frame — which is every HTTP
   * response — declares everything, so it replaces, which is this view's
   * behaviour unchanged.
   */
  const applySnapshot = useCallback(
    (frames: readonly ScreenerFrame[], columns: ScreenerColumnMeta[]) => {
      let next = applyScreenerPush(mergeBase.current, frames);
      // The wave-2 push that was waiting for a roster now has one. Folded here
      // rather than replayed through this callback so there is still exactly one
      // merge, in one order: the roster first, then the columns over it.
      const pending = pendingScan.current;
      if (pending) {
        pendingScan.current = null;
        next = applyScreenerPush(next, pending);
      }
      mergeBase.current = next;
      setGroupFrames(next);
      // Only when the payload carried metadata. A wave-2 push says nothing about
      // labels or timeframes, and taking its empty `columns` frame at face value
      // would erase the dictionary wave 1 delivered.
      if (columns.length > 0) {
        heldMeta.current = columns;
        setGroupMeta(columns);
      }
      setStatus("open");
      setRefreshing(false);
      fetched.current = true;
      onSchema?.({
        // The **merged** fields, so a computed column that arrived in a later
        // wave is offered by the docked editor — the payload alone only ever
        // knows about half a screen.
        fields: next.find((f) => f.name === "detail")?.fields ?? [],
        meta: heldMeta.current,
        timings: readScreenerTimings(next),
      });
    },
    [onSchema],
  );

  /**
   * Drop the merge baseline when the *question* changes.
   *
   * A different roster or a different set of expressions is a different answer,
   * and a value computed against the previous one is a plausible wrong number
   * rather than a missing one — the field name can be identical while the
   * expression behind it is not. So the next payload replaces instead of
   * merging.
   *
   * Only the *baseline* is dropped, never `groupFrames`: the rows on screen stay
   * until the new ones arrive, exactly as they did when this was a fetch. That
   * is the same rule as everything else here — nothing blanks while it waits.
   */
  useEffect(() => {
    mergeBase.current = null;
    // And anything wave 2 pushed for the old question with it: a held payload
    // is a *deferred* merge, so keeping it would apply the previous group's
    // computed values onto the next group's rows by symbol — the plausible
    // wrong number this reset exists to prevent.
    pendingScan.current = null;
  }, [codesKey, computedKey, sealedKey]);

  /**
   * A new Recs bar or kind empties the previous one's trade, at once.
   *
   * Not the baseline drop above: the roster and the other columns are the same
   * answer, and wave 1 is not asked again, so dropping the baseline would only
   * strand wave 2. And not "keep it until the new push": a bar whose inputs are
   * still computing pushes no `rec*` at all, so W's list stood under the D
   * button (2026-10-20). Empty is true — nothing is known yet for this bar.
   */
  const recsAnswerKey = recsKey(recsInForce);
  const recsAnswerSeen = useRef(recsAnswerKey);
  useEffect(() => {
    if (recsAnswerSeen.current === recsAnswerKey) return;
    recsAnswerSeen.current = recsAnswerKey;
    if (!mergeBase.current) return;
    const cleared = withoutRecs(mergeBase.current);
    if (cleared === mergeBase.current) return;
    mergeBase.current = cleared;
    setGroupFrames(cleared);
  }, [recsAnswerKey]);

  const handleStreamStatus = useCallback((next: ConnectionStatus) => {
    // `applySnapshot` overwrites this with "open" the moment rows land, so this
    // shows while there are none — and after a drop, which is exactly when a
    // stale screen has to say so rather than passing for a live one.
    if (next === "closed") setStatus("disconnected — retrying…");
    else if (next === "connecting") setStatus("connecting…");
  }, []);

  const handleStreamError = useCallback((err: TransportError) => {
    // A socket error is transient and the client is already retrying, so it
    // reaches the status line through `onStatus` and no further: raising it as
    // *the* error would put "Screener unavailable" over rows that are still
    // perfectly readable. A decode failure is the other kind — the payload
    // itself is wrong, and retrying does not fix that.
    if (err.source === "decode") setError(err.message);
  }, []);

  const handlePush = useCallback(
    (snapshot: ScreenerRosterSnapshot) => applySnapshot(snapshot.frames, snapshot.columns),
    [applySnapshot],
  );

  // The subscription. Inert when `VITE_QV_SCREEN_URL` is unset, in which case
  // the fetch effect below does the delivery instead — see the header.
  useScreenStream({
    codes,
    mode: selection.combine === "and" ? "intersect" : "union",
    computed: serverComputed,
    sealedConditions: sealed,
    ...(stratCode && stratMonth ? { strategyMonth: stratMonth } : {}),
    key: `${codesKey}|${computedKey}|${sealedKey}${stratKey}`,
    refreshNonce,
    onSnapshot: handlePush,
    onStatus: handleStreamStatus,
    onError: handleStreamError,
  });

  /**
   * Wave 2 — the computed columns, from `qf-scr`.
   *
   * Lands in `applySnapshot` exactly as wave 1 does, so the merge rule stays in
   * one place. It carries **no** `columns` frame of its own worth trusting: the
   * dictionary is wave 1's, and `applySnapshot` already refuses to let a later
   * wave erase it.
   *
   * Inert when `VITE_QV_SCAN_URL` is unset, which is what shipped before
   * `qf-scr` existed — the grid is then wave 1 only and nothing says otherwise.
   */
  const handleScanFrames = useCallback(
    (frames: readonly ScreenerFrame[]) => {
      // **Nothing arriving here may own the row set, and the payload has to say
      // so itself.** `applyScreenerPush` reads the `push` frame to learn which
      // columns a payload speaks for, and a payload with no `push` frame
      // declares *everything* — which is right for the single HTTP response it
      // was written for and catastrophic on this socket, where the detail frame
      // holds the id column and the computed ones and nothing else. So a
      // payload that does not declare itself wave 2 is dropped rather than
      // trusted: `routers/scan.py` stamps that frame and says why.
      const push = readScreenerPush(frames);
      if (!push || push.carriesRoster) return;
      // No roster to merge into yet — hold it. See `pendingScan`.
      if (!mergeBase.current) {
        pendingScan.current = frames;
        return;
      }
      applySnapshot(frames, []);
    },
    [applySnapshot],
  );

  /** Wave 2's socket state, so a screen that lost it does not read as live. */
  const [scanStatus, setScanStatus] = useState<ConnectionStatus>("idle");

  /**
   * Stored columns this screen shows that wave 1 handed to wave 2 — the Impulse
   * (JAS) columns while the market is open (`source: compute` on a field the
   * config does not compute). Asked for by name, and only when shown: the
   * owner's choice, so a member receives what is on screen and no more.
   */
  const liveStored = useMemo(
    () => liveStoredFields(config.columns, config.filter, readColumnSources(activeFrames)),
    [activeFrames, config.columns, config.filter],
  );
  const liveKey = liveStored.join(",");

  /**
   * The ticked sealed conditions wave 1 handed to wave 2 — the ones whose
   * `field` its `columns` frame marks `source: compute`, decided exactly as
   * `liveStored` is. Only these go to `qf-scr`, so the two tiers never answer
   * the same field; wave 1 is still asked for every one and answers the rest.
   */
  const sealedLive = useMemo(() => {
    if (sealed.length === 0) return sealed;
    const sources = readColumnSources(activeFrames);
    return sealed.filter((s) => sources.get(s.field) === "compute");
  }, [activeFrames, sealed]);
  const sealedLiveKey = sealedLive.map((s) => s.field).join(",");

  useScanColumns({
    codes,
    mode: selection.combine === "and" ? "intersect" : "union",
    computed: serverComputed,
    live: liveStored,
    sealedConditions: sealedLive,
    // A JP trade-list screen: its frequency is the server's question, its side
    // is the grid's (`lib/recs.ts`), so only the first is in the key.
    recs: recsAsk(recsInForce),
    key: `${codesKey}|${computedKey}|${liveKey}|${sealedLiveKey}|${recsKey(recsInForce)}`,
    nonce: refreshNonce,
    onFrames: handleScanFrames,
    onStatus: setScanStatus,
  });

  /**
   * Whether PD's columns may be shown at all.
   *
   * The socket pushes **every** column and show/hide is a client-side view over
   * what arrived — `routers/scr.py` says so at the top of the file — so this
   * withholds them from the picker and from the grid without pretending to be
   * the gate. The gate for these metrics is the chart's, on the server.
   */
  const pd = can("ind:PD");

  /** Server metadata plus the columns the user authored. */
  const panelMeta = useMemo(
    () => mergedMeta(groupMeta, config.columns),
    [groupMeta, config.columns],
  );

  /**
   * The config the grid actually renders.
   *
   * Identical to `config` except that switching filters off drops the effective
   * `filter` node — see `filtersEnabled`. `activeFilterRefs` is deliberately
   * left alone, so the picker still shows what is ticked and turning filters
   * back on restores exactly that selection.
   */
  const effectiveConfig = useMemo<ScreenerConfig>(() => {
    // Each ticked sealed condition's flag ANDed in **here, on the way to the
    // grid, and never written back** — `config.filter` holds only the rules this
    // caller can read, so nothing the section, the layer or a save carries could
    // pass for the group's (`sealed-conditions.ts`).
    const filtered =
      sealed.length > 0 ? { ...config, filter: withSealedConditions(config.filter, sealed) } : config;
    // A trade list's side is ANDed in last and survives filters being off:
    // it is which list you are reading, not a condition (`lib/recs.ts`).
    let shown = recsInForce
      ? {
          ...config,
          filter: withRecsDirection(filtersEnabled ? filtered.filter : undefined, recsInForce),
        }
      : filtersEnabled
        ? filtered
        : { ...config, filter: undefined };
    // A TopDown's ticks, last of all and on the grid's copy only — the drill is
    // not the funnel's to switch off. The base screen's stored filter is never
    // ANDed in: only what is ticked narrows (owner, 2026-10-18).
    if (topDown) {
      shown = { ...shown, filter: withTicks(shown.filter, topDown.ticks) };
    }
    // The bar's ▲▼ key: its own switch, so it survives filters being off, and
    // like the rest it reaches the grid's copy only (docs/plans/movers.md).
    if (config.direction) {
      const field = directionField(config.sort);
      shown = {
        ...shown,
        filter: withDirection(shown.filter, config.direction, field),
        sort: directionSort(shown.sort, config.direction, field),
      };
    }
    // A Strat screen is the strategy's rows, all of them, heaviest first, in
    // the owner's columns — the grid's copy only; `config` is never rewritten.
    if (stratCode) {
      return {
        ...shown,
        columns: strategyColumns(shown.columns),
        filter: undefined,
        sort: [{ field: "stratWeight", dir: "desc" }],
      };
    }
    // `strat*` columns are blank off a Strat list, so they are hidden there —
    // saved on the screen for when one is on again (owner, 2026-10-27).
    if (shown.columns) {
      const kept = withoutStratColumns(shown.columns);
      if (kept !== shown.columns) shown = { ...shown, columns: [...kept] };
    }
    if (pd) return shown;
    // **Dropped on the way to the grid, never written back.** A saved screen
    // that shows `composite` keeps it in `config`, so the column returns intact
    // the day the account is granted PD -- filtering the *stored* view would
    // delete somebody's column as a side effect of an entitlement lapsing.
    //
    // A computed column whose expression names a PD field is out of scope here:
    // it is authored text, evaluated client-side over a frame that carries the
    // value regardless, and hiding one would need the DSL to be read rather
    // than the field list.
    const columns = shown.columns?.filter((column) => !isPdField(column.field));
    return columns && columns.length !== (shown.columns?.length ?? 0)
      ? { ...shown, columns }
      : shown;
  }, [config, filtersEnabled, pd, sealed, topDown?.ticks, stratCode]);
  const baseGridFrames = useMemo(
    () => (stratCode ? withEntryText(activeFrames, stratMonth ?? stratWhy?.month.slice(0, 7)) : activeFrames),
    [stratCode, activeFrames, stratMonth, stratWhy?.month],
  );

  // ── the Leaders panel (docs/plans/leader-screener.md, step 5) ─────────────
  //
  // Over an ordinary screen only: a TopDown, a Seasonality screen, a Strat list
  // and the phone's rows each lay out their own grid. View state — not saved
  // with the screen in phase 1 (plan O-8), so every open starts on Latest.
  // Offered with `feat:boxes` (admin, then mdonc, then everyone — owner,
  // 2026-11-03) and the capability that holds `ergRank`; the server refuses
  // without either (403), this only keeps the key off a bar it cannot use.
  const leadersOffered =
    leadersConfigured && !frameGrid && !topDown && !renderRows && !stratCode &&
    can("feat:boxes") && can("ind:Universe Rank");
  const [leadersOn, setLeadersOn] = useState(false);
  const leadersActive = leadersOffered && leadersOn;
  const [leaders, setLeaders] = useState<LeadersSettings>({
    threshold: DEFAULT_THRESHOLD, view: "boxes", date: null, selection: new Set(),
  });
  const [leadersSort, setLeadersSort] = useState<ScreenerConfig["sort"]>([{ field: LEADER_FIELDS.erg, dir: "desc" }]);
  const [leadersAnswer, setLeadersAnswer] = useState<LeadersAnswer | null>(null);
  const [leadersError, setLeadersError] = useState<string | null>(null);
  const [leadersLoading, setLeadersLoading] = useState(false);
  const leaderSymbols = useMemo(() => {
    const detail = activeFrames.find((f) => f.name === "detail");
    return detail ? detail.rows.map((r) => String(r.symbol ?? "")).filter(Boolean).sort() : [];
  }, [activeFrames]);
  const leaderKey = leaderSymbols.join(",");
  useEffect(() => {
    if (!leadersActive || leaderSymbols.length === 0) return;
    const abort = new AbortController();
    setLeadersLoading(true);
    // Settled, so ◀ ◀ ◀ asks once for where it stopped, and a refresh that
    // re-sends the same rows asks nothing new.
    const timer = window.setTimeout(() => {
      void fetchLeaders(leaderSymbols, leaders.date, abort.signal).then((result) => {
        if (abort.signal.aborted) return;
        setLeadersLoading(false);
        if (result.ok) {
          setLeadersAnswer(result.answer);
          setLeadersError(null);
        } else setLeadersError(result.reason);
      });
    }, 250);
    return () => {
      window.clearTimeout(timer);
      abort.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadersActive, leaderKey, leaders.date]);
  const leadersView = useMemo(
    () => (leadersAnswer ? leadersOf(leadersAnswer, leaders.threshold) : null),
    [leadersAnswer, leaders.threshold],
  );
  const leadersPast = leaders.date !== null ? (leadersAnswer?.resolvedDate ?? leaders.date) : null;
  const gridFrames = useMemo(
    () =>
      leadersActive && leadersView
        ? applyPanel(baseGridFrames, leadersView.leaders, leaders.selection, leadersPast)
        : baseGridFrames,
    [leadersActive, leadersView, baseGridFrames, leaders.selection, leadersPast],
  );
  const gridConfig = useMemo(
    () =>
      leadersActive
        ? withPanelColumns({ ...effectiveConfig, sort: leadersSort ?? [] }, leadersPast)
        : effectiveConfig,
    [leadersActive, effectiveConfig, leadersSort, leadersPast],
  );

  // ── TopDown: the base rows its filter keeps, before the ticks ──────────────
  const onMembers = topDown?.onMembers;
  const memberFilter = useMemo(() => {
    if (!onMembers || !filtersEnabled) return undefined;
    return sealed.length > 0 ? withSealedConditions(config.filter, sealed) : config.filter;
  }, [onMembers, config.filter, filtersEnabled, sealed]);
  useEffect(() => {
    if (!onMembers) return;
    const detail = activeFrames.find((f) => f.name === "detail");
    if (!detail) return;
    const text = (v: unknown) => (typeof v === "string" && v ? v : null);
    onMembers(
      detail.rows
        .filter((row) => matchesFilter(memberFilter, row))
        .map((row) => ({
          symbol: String(row.symbol ?? ""),
          sector: text(row.sector),
          industry: text(row.industry),
          // What a level adds up, QuantVitals' way (`memberAggregates`).
          values: Object.fromEntries(
            MEMBER_FIELDS.map((f) => [f, typeof row[f] === "number" ? (row[f] as number) : null]),
          ),
        }))
        .filter((row) => row.symbol),
    );
  }, [onMembers, activeFrames, memberFilter]);

  const hasFilters = useMemo(() => selectedFilterIds(config).length > 0, [config]);

  // ── track lists ────────────────────────────────────────────────────────────
  //
  // Declared above the catalog effect, because a list is *in* that catalog: the
  // bar's chips for them are synthesised per caller by `/sym/groups`, so the
  // fetch below has to know when one is created. Everything derived from these
  // is further down, beside the panel that uses it.

  const { lists: trackLists, version: trackVersion, enabled: trackEnabled } = useTrackLists();

  /**
   * Fill the panel from a saved screen — the shared step, see `lib/screen-sync`.
   *
   * The tab strip in the editor calls the same function; this one is here
   * because the refill has to happen with the editor shut.
   */
  const appliedKey = useRef<string | null>(null);
  /**
   * The club screen on screen, and what it was assembled from.
   *
   * Held because `splitOverlay` needs the **base** to diff against: the panel
   * alone cannot say which columns were the club's. Null for a screen you own,
   * where there is no base to diff against — the definition *is* yours.
   */
  const [linked, setLinked] = useState<{
    key: string;
    base: ScreenBase;
    overlay: Overlay;
  } | null>(null);

  /**
   * The group being authored as — `/auth/me`'s `publishTo`, through the shared
   * entitlements store, so this costs no request of its own. Null for everybody
   * not in Author as, and every draft path below starts by checking it.
   */
  const authoredKeyOf = useCallback(
    (key: string | undefined): boolean => {
      // `publishTo` first, so a panel nobody is authoring never consults the
      // catalog for this at all.
      if (!publishTo || !key) return false;
      const screen = screens.find((s) => s.key === key);
      return screen !== undefined && isAuthored(screen, publishTo);
    },
    [publishTo, screens],
  );
  const authored = authoredKeyOf(config.screenRef);
  /** The screen last filled *as authored* — see the refill effect. */
  const authoredKey = useRef<string | null>(null);
  /** An authored screen whose draft could not be read: nothing is being saved. */
  const [draftUnread, setDraftUnread] = useState<string | null>(null);
  const [draftRetrying, setDraftRetrying] = useState(false);

  const applyScreen = useCallback(
    async (key: string, keepPanel = false) => {
      appliedKey.current = key;
      // **The group's own screen, while authoring as the group: its draft, else
      // the published screen — whole, and never through the overlay.** Owner,
      // 2026-10-06 (`docs/plans/author-screen-drafts.md`, decision 3): the
      // draft takes everything, widths and order included, so the author's own
      // layer has no say over a screen they are authoring. It was the overlay
      // that lost the formats: it has nowhere to keep one.
      if (authoredKeyOf(key)) {
        authoredKey.current = key;
        setLinked(null);
        // Nothing on the panel is an edit until this load answers.
        forgetBaseline(key);
        let loaded: Awaited<ReturnType<typeof loadAuthored>>;
        try {
          loaded = await loadAuthored(key);
        } catch {
          // Unreadable: leave the panel alone and write nothing. Filling from
          // the published screen here is how a draft gets overwritten. Said
          // out loud (`DraftUnreadBar`) — silent, edits looked saved and were not.
          setDraftUnread(key);
          return;
        }
        setDraftUnread((was) => (was === key ? null : was));
        if (!loaded) return;
        let filled = false;
        await applyScreenTo({
          key,
          config: stored,
          load: async () => loaded,
          onConfigChange: (patch) => {
            filled = true;
            // The baseline is the panel *after* this patch — the reducer is a
            // spread, so this is exactly what `definitionOf` will read next.
            markBaseline(key, { ...stored, ...patch });
            onConfigChange?.(patch);
          },
        });
        if (!filled) markBaseline(key, stored);
        return;
      }
      authoredKey.current = null;
      const own = screens.some((s) => s.key === key && s.ownerKind === "user");
      if (own) {
        setLinked(null);
        await applyScreenTo({
          key,
          config: stored,
          load: fetchPreset,
          onConfigChange: (patch) => onConfigChange?.(patch),
        });
        return;
      }
      // **A club screen is the base plus your layer, and both are fetched.**
      // The base fresh every time, so the club's edits arrive; the layer so the
      // columns you added and the widths you set are still there.
      const [screen, overlay] = await Promise.all([fetchPreset(key), fetchOverlay(key)]);
      if (!screen) return;
      const base: ScreenBase = {
        columns: screen.columns ?? [],
        filter: screen.filter,
        sort: screen.sort ?? [],
        // A screen that predates the library (`null`) uses the panel's own.
        savedFilters: screen.savedFilters ?? (stored.savedFilters ?? []).filter((f) => !f.link),
      };
      setLinked({ key, base, overlay });
      // **On a page load the panel's own layer wins** (`keepPanel`). Edits made
      // while no layer was written live only in the section — hiding Name, a
      // condition of the member's own — and merging the stored layer instead
      // brought Name back and dropped the condition. Read out of the panel, they
      // are drawn over the fresh base and `useOverlaySync` writes them up.
      const layer = keepPanel
        ? splitOverlay(base, definitionOf(stored), overlay)
        : overlay;
      const merged = panelOf(base, layer);
      const patch = {
        columns: merged.columns,
        filter: merged.filter,
        sort: merged.sort,
        // The group's library, then the member's own and linked conditions —
        // **from the published screen on every load**, as the columns are. It
        // used to be refreshed only when the layer held links, so a member kept
        // the library copied in when they first opened the tab: Impulse was
        // unsealed and mdonc still saw 🔒 and asked the server (2026-10-19).
        savedFilters: merged.savedFilters ?? [],
      };
      if (keepPanel) {
        // Nothing dispatched when the panel already says it — a page load is a
        // read, not a write.
        if (sameDefinition(definitionOf(stored), definitionOf({ ...stored, ...patch }))) return;
        onConfigChange?.(patch);
        return;
      }
      onConfigChange?.({
        screenRef: key,
        ...patch,
        // As the group saved it: the layer carries no ▲▼, so a member's own
        // choice lasts on the panel until the tab is picked again.
        direction: screen.direction,
      });
    },
    [stored, onConfigChange, screens, authoredKeyOf],
  );

  /**
   * The screen is master, so the panel is refilled from it when the tab changes.
   *
   * Keyed on the **ref**, not on the config, and guarded by a ref of its own:
   * `applyScreen` closes over the config and so changes identity on every edit,
   * and an effect that re-ran on that would refill the panel from the server
   * under somebody's typing.
   *
   * This is also the mount path — a page load refills from the row rather than
   * trusting the section's copy, which is what makes an edit made on another
   * device show up here. `applyScreen` dispatches nothing when the two already
   * agree, so the ordinary load costs a read and no write.
   */
  useEffect(() => {
    const key = config.screenRef;
    // **An authored screen is refilled on mount too**, from its draft — that is
    // the reported bug's fix: the section's copy is not where an author's
    // formats live any more. Keyed on `authoredKey` rather than `appliedKey`
    // because `publishTo` and the catalog both arrive after first render: the
    // panel first sees *Persons* as a club's screen (skipped, below) and must
    // refill once it learns it is authoring it.
    if (key && authoredKeyOf(key)) {
      if (authoredKey.current !== key) void applyScreen(key);
      return;
    }
    if (!key || appliedKey.current === key) return;
    // **On mount, a club's screen is linked, keeping the panel.** It used to be
    // left alone entirely, so `linked` stayed null and **nothing the member did
    // for the rest of the session reached their layer** — hide Name, reload,
    // pick the tab again, and Name was back (owner, 2026-10-30). Now the base
    // and the layer are fetched and the panel's own view is kept over them.
    // Waits for the catalog: before it, every screen looks like a club's.
    const mine = screens.find((s) => s.key === key && s.ownerKind === "user");
    if (appliedKey.current === null && !mine) {
      if (screens.length === 0) return;
      void applyScreen(key, true);
      return;
    }
    void applyScreen(key);
  }, [config.screenRef, applyScreen, screens, authoredKeyOf]);

  /**
   * The bar's two screens. Current is the section's own `screenRef` (or the
   * strip's fallback); previous is whatever was current before it, **however**
   * it changed — the bar, the strip key's list or the editor's tabs — so it is
   * read off the change rather than recorded by one caller. Remembered per
   * browser beside the group selection (screener-bar plan, decision 1).
   */
  const host = screenHost?.config ?? stored;
  const currentScreen = (screenHost ? host : config).screenRef ?? defaultScreen(screens)?.key;
  const screenName = screens.find((s) => s.key === currentScreen)?.name;
  // The screens before it, newest first, per panel (owner, 2026-10-16) — the
  // bar shows as many as fit (`recentScreens`).
  const [recentList, setRecentList] = useState(() => recallRecent(host.id));
  // Seeded from storage, not from this mount's first screen: a TopDown and a
  // plain panel are different views, so switching between them remounts this
  // one and the screen before would otherwise never be seen (`recallCurrent`).
  const lastScreen = useRef(recallCurrent(host.id) ?? currentScreen);
  useEffect(() => {
    const was = lastScreen.current;
    if (!currentScreen) return;
    lastScreen.current = currentScreen;
    rememberCurrent(host.id, currentScreen);
    if (!was || was === currentScreen) return;
    setRecentList(rememberRecent(host.id, was));
  }, [currentScreen, host.id]);

  /**
   * Switch screen from the bar — `applyScreenTo` with the draft-aware loader,
   * the same call the strip key's hover list makes in `App`. The refill effect
   * above then does what it does for any change of `screenRef` (a club's layer).
   */
  const pickScreen = useCallback(
    (key: string) =>
      void applyScreenTo({
        key,
        config: host,
        load: linkLoad,
        onConfigChange: (patch) => (screenHost ? screenHost.onConfigChange : onConfigChange)?.(patch),
      }),
    [host, screenHost, linkLoad, onConfigChange],
  );

  /**
   * The panel's edits, to the draft of a screen this account is authoring — see
   * `lib/screen-draft`. Declared after the refill effect on purpose: effects run
   * in order, so a tab change forgets the old baseline before this looks at it.
   *
   * Exclusive with the two below by construction: an authored screen is `org`,
   * so `useScreenSync` (user screens only) never writes it, and `applyScreen`
   * clears `linked` for it, so `useOverlaySync` has nothing to write.
   */
  useDraftSync({ config: stored, authored });
  const draft = useDraftMeta(authored ? config.screenRef : undefined);
  const [draftBusy, setDraftBusy] = useState(false);
  const [draftError, setDraftError] = useState<string | null>(null);
  const authoredScreen = authored ? screens.find((s) => s.key === config.screenRef) : undefined;

  /**
   * Discard: asked first — it cannot be undone — then the draft is deleted and
   * the panel refilled from the published screen, marked as the new baseline so
   * the refill is not mistaken for an edit and written back as a fresh draft.
   */
  const handleDiscard = useCallback(async () => {
    const key = config.screenRef;
    if (!key || !authoredScreen) return;
    if (!window.confirm(`Discard your unpublished changes to ${authoredScreen.name}?`)) return;
    setDraftBusy(true);
    setDraftError(null);
    try {
      await discardAuthored(key);
      // Straight through `applyScreen`, which finds no draft now and fills from
      // the published screen. `authoredKey` is left as it is, so the refill
      // effect — woken by the catalog's ✎ changing — does not load it twice.
      await applyScreen(key);
    } catch (cause) {
      setDraftError(cause instanceof Error ? cause.message : "the draft was not discarded");
    } finally {
      setDraftBusy(false);
    }
  }, [config.screenRef, authoredScreen, applyScreen]);

  /** Publish: to the screen's own key; the bar goes and the version moves. */
  const handlePublish = useCallback(async () => {
    const key = config.screenRef;
    if (!key) return;
    setDraftBusy(true);
    setDraftError(null);
    try {
      await publishAuthored(key);
    } catch (cause) {
      setDraftError(cause instanceof Error ? cause.message : "publish failed");
    } finally {
      setDraftBusy(false);
    }
  }, [config.screenRef]);

  /**
   * The panel's edits, back to the *layer* when the screen is somebody else's.
   *
   * The two syncs are mutually exclusive by construction: `useScreenSync` writes
   * only a screen whose `ownerKind` is `user`, and this one runs only while
   * `linked` is set, which happens only for a screen that is not. Ownership
   * decides, so an edit can never be written twice or written to the wrong half.
   */
  useOverlaySync({
    linked,
    panel: definitionOf(stored),
    screenRef: stored.screenRef,
    save: saveOverlay,
    // Keep the held layer in step with what was written, or the next diff is
    // taken against a baseline the server no longer holds.
    onSaved: useCallback(
      (key: string, overlay: Overlay) =>
        setLinked((held) => (held && held.key === key ? { ...held, overlay } : held)),
      [],
    ),
  });

  /**
   * The panel's edits, back to the screen it is showing — see `lib/screen-sync`.
   *
   * The screen's **stored** name, market and group codes travel with every
   * write: the route replaces rather than merges, so a field left out is cleared
   * rather than kept, and the universe is not something this panel edits.
   */
  useScreenSync({
    config: stored,
    screens,
    save: useCallback(
      async (key, definition) => {
        const screen = screens.find((s) => s.key === key);
        if (!screen) return;
        await saveScreen(key, {
          name: screen.name,
          market: screen.market,
          groupCodes: screen.groupCodes,
          ...definition,
        });
      },
      [screens],
    ),
  });
  const trackListCount = trackLists.length;

  /** The star that is open, and what its ticks will act on. */
  /** A Movers News cell's list, open under the cell that was clicked. */
  const [newsList, setNewsList] = useState<{
    symbol: string;
    window: NewsWindow;
    anchor: DOMRect;
  } | null>(null);
  const openNews = useCallback(
    (symbol: string, window: NewsWindow, anchor: DOMRect) => setNewsList({ symbol, window, anchor }),
    [],
  );
  const closeNews = useCallback(() => setNewsList(null), []);
  const [listMenu, setListMenu] = useState<{
    symbols: string[];
    anchor: DOMRect;
  } | null>(null);
  /** The create dialog, carrying the symbols that prompted it — none, from the bar. */
  const [newList, setNewList] = useState<{ symbols: string[]; over: number } | null>(null);
  const [newListError, setNewListError] = useState<string | null>(null);
  const [newListBusy, setNewListBusy] = useState(false);

  const openNewList = useCallback((symbols: string[] = []) => {
    setNewListError(null);
    // Deduped and cut at the list's cap here, so the dialog's count is what
    // the list will hold and the rest are named before Create, not refused after.
    setNewList(seedNewList(symbols));
  }, []);

  /**
   * The catalog. `symbolsConfigured` false ⇒ the bar never shows and this is a
   * no-op, matching how every other optional service degrades.
   *
   * **Re-fetched when the caller's own lists change**, which is the one thing in
   * this catalog that is not the same for everybody and not fixed for the
   * session. `/sym/groups` synthesises a list's chip per caller, so a list
   * created from the popover would otherwise not appear in the bar — and would
   * not appear *until a reload*, which reads as the create having failed. The
   * other four types are seeded rows that cannot change under a running page,
   * so this costs one extra request per create or delete and nothing per tick.
   */
  useEffect(() => {
    if (!symbolsConfigured) return;
    let disposed = false;
    void fetchGroupTypes().then((t) => {
      if (!disposed && t) setTypes(t);
    });
    // Every group in one request, not one request per type. Multi Select offers
    // all of them at once, and four round trips to populate four menus would
    // make the first one opened the slow one for no reason.
    void fetchGroups().then((g) => {
      // **Locked groups are dropped here, not rendered greyed.** The bar is a
      // working set, not a shop window: a chip you cannot pick still costs a
      // slot in a strip that already runs out of width, and picking one led
      // straight to "not included in your plan" where the screener should be.
      // The upsell belongs somewhere that is not in the way of the work.
      if (!disposed && g) setAllGroups(g.filter((each) => each.locked !== true));
    });
    return () => {
      disposed = true;
    };
    // `trackListCount` rather than `trackVersion`: a version bump also fires on
    // every *membership* change, and re-fetching the whole catalog on each star
    // click would put a request behind a gesture that is meant to cost nothing.
    // What the bar needs to hear about is a list appearing or disappearing.
  }, [trackListCount]);

  // Remember the choice, so reopening lands where the user left off.
  useEffect(() => {
    rememberSelection(config.id, selection);
  }, [config.id, selection]);

  /**
   * A browser that has never chosen a group gets the first one.
   *
   * The selection lives in `localStorage`, so "no group" is the state of every
   * *new device* — and no group now screens nothing. Without this a new phone
   * would open on "No group picked" beside a desktop showing rows, which reads
   * as broken rather than as a choice nobody has made yet.
   *
   * Runs once the catalog lands, and only while nothing is chosen, so it can
   * never overwrite a real selection — including the deliberate empty one a
   * user reaches by unpicking their last Multi Select chip. That empty state is
   * the one this seed must *not* fill: the user asked for it.
   */
  /**
   * **Once**, when the catalog lands, and never again.
   *
   * That timing is what makes `seedSelection`'s reading of the state sound —
   * see its docstring. The type menu that can clear a group is built from this
   * same catalog, so at this moment "single select, no group" can only mean
   * nobody has chosen yet.
   */
  const seeded = useRef(false);
  useEffect(() => {
    if (seeded.current || allGroups.length === 0) return;
    seeded.current = true;
    setSelection((was) => seedSelection(allGroups, was) ?? was);
  }, [allGroups]);

  // The screen, fetched for whatever the bar has picked. Nothing picked is not
  // a degraded case with a second source behind it — it is the empty screen,
  // and the last group's rows must not be left on it.
  useEffect(() => {
    if (codes.length === 0) {
      setGroupFrames(null);
      mergeBase.current = null;
      // The empty screen is the answer, so a wave-2 payload held for the group
      // that was on it a moment ago has nothing left to merge into.
      pendingScan.current = null;
      setError(null);
      setStatus("no group");
      return;
    }
    setError(null);

    // Only the run the button caused drops the server's cache — see the nonce.
    const forced = refreshNonce !== seenRefresh.current;
    seenRefresh.current = refreshNonce;

    // Named by their codes, not by a count. "SP1500 + MEGA" is what the user
    // just clicked and what they would retype; "2 groups" is a number they have
    // to look back at the bar to decode.
    const what = codes.join(selection.combine === "and" ? " ∩ " : " ∪ ");
    // Said before the wait, not after it, so a debounced request still reads as
    // the app doing something. The alternative — a silent 300ms — looks like a
    // click that did not land. Said for the subscription too, which waits the
    // same 300ms for the same reason.
    setStatus(forced ? `refreshing ${what}…` : `loading ${what}…`);

    // The subscription owns delivery wherever one is configured. Left as an
    // early return rather than a second branch: the two paths differ only in
    // what carries the bytes, and every rule about what a payload *means* lives
    // in `applySnapshot`, which both of them call. It sits after the status and
    // the error clear because those describe *the screen*, not the transport.
    if (screenStreamConfigured) return;
    let disposed = false;

    const run = () => {
      void fetchGroupsScreen(
        codes,
        selection.combine,
        serverComputed,
        forced,
        sealed,
        stratCode ? stratMonth : undefined,
      ).then((result) => {
        // `refreshing` is cleared even when disposed: it is button state, not
        // frame state, and a group switched mid-refresh would otherwise leave
        // the button saying "Refreshing…" for a request nobody is waiting on.
        setRefreshing(false);
        if (disposed) return;
        if (!result.ok) {
          // The server's own words, not a guess at them. Every failure here
          // used to read "no screen data", which was equally true of a group
          // that does not exist and of a server too old to have the route — and
          // the second of those is a restart, not a bug.
          setError(`${what}: ${result.reason}`);
          setGroupFrames([]);
          // And the merge baseline with them: a refusal clears the screen, so a
          // later payload must replace rather than merge onto rows nobody can
          // see any more. A held wave-2 push goes the same way, for the same
          // reason.
          mergeBase.current = null;
          pendingScan.current = null;
          return;
        }
        applySnapshot(result.snapshot.frames, result.snapshot.columns);
      });
    };

    // Refresh and the first load go straight out: one is an explicit request
    // for *now*, the other has nothing before it to coalesce with, and both
    // would only feel slow. Everything else waits, so a run of ticks in the
    // tick-list is one screen build rather than five — see the constant.
    if (forced || !fetched.current) {
      run();
      return () => {
        disposed = true;
      };
    }

    const timer = setTimeout(run, SELECTION_DEBOUNCE_MS);
    return () => {
      disposed = true;
      // The cancel *is* the debounce: the next tick re-runs this effect, and
      // its teardown drops the request the previous one had queued.
      clearTimeout(timer);
    };
    // `computedKey`, not `serverComputed`, and for the socket effect's reason:
    // the array identity is fresh on every patch, so keying on it would refetch
    // the whole group on an unrelated column resize. Its **content** is what
    // changes the server's answer — and without it here at all, adding a column
    // did not refetch, which is why one was blank on every group.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codesKey, computedKey, sealedKey, refreshNonce, stratKey]);

  // Only re-notify on identity, so a patch dispatch doesn't retrigger the effect.
  const handleConfigChange = useCallback(
    (patch: ScreenerConfigPatch) => {
      if (!leadersActive) {
        onConfigChange?.(patch);
        return;
      }
      // **The panel's columns and its sort never reach the screen.** They are
      // the panel's view of the rows; saving them would put `ldErg` into a
      // screen that has no panel the next time it opens.
      let next: ScreenerConfigPatch = patch;
      if (patch.sort) {
        setLeadersSort(patch.sort);
        const { sort: _sort, ...rest } = next;
        next = rest;
      }
      if (patch.columns) {
        next = { ...next, columns: patch.columns.filter((c) => !isPanelField(c.field)) };
      }
      if (Object.keys(next).length) onConfigChange?.(next);
    },
    [onConfigChange, leadersActive],
  );

  /**
   * Stable, and it has to be: the grid takes this as `onModelUpdated`, so a new
   * function each render re-registers the handler, which fires it, which sets
   * state, which renders again. An inline arrow here was an infinite loop.
   *
   * `null` when nothing is filtered out, so the count is the plain total rather
   * than a filtered number that happens to equal it.
   */
  const handleRowCounts = useCallback((matched: number, total: number) => {
    setMatchedRows((previous) => {
      const next = matched === total ? null : matched;
      return previous === next ? previous : next;
    });
  }, []);

  const totalRows = useMemo(
    () => gridFrames.find((f) => f.name === "detail")?.rows.length ?? 0,
    [gridFrames],
  );

  /**
   * The number on the bar's filter toggle.
   *
   * Deliberately one number and not `29 / 1000`: what a filter *kept* is the
   * thing that changes, and the total is one click away on the same button.
   * `null` before anything has loaded, which renders as a dash rather than a
   * confident zero.
   */
  const barCount = activeFrames.length === 0 ? null : (matchedRows ?? totalRows);

  const statusLine = useMemo(() => {
    if (error) return `error: ${error}`;
    const wave1 = status === "open" ? "" : status;
    // **Wave 2 is a note, never the screen's status.** A screen with no computed
    // columns is a wave 1 screen and says nothing; one that asked for them and
    // lost the socket is showing values from whenever it last had it, and
    // nothing else on screen tells those two apart. Said only while there is
    // something to say it about — `scanConfigured` and a column that needs the
    // tier — so an unconfigured build is silent exactly as it was.
    const wave2 =
      scanConfigured && serverComputed.length > 0 && codes.length > 0 && scanStatus === "closed"
        ? "computed columns — retrying…"
        : "";
    return [wave1, wave2].filter(Boolean).join(" · ");
  }, [error, status, scanStatus, serverComputed.length, codes.length]);

  /**
   * The list the Add row files into — only while the screen is exactly one list
   * of the user's own. `listForScreen` carries the reason; an index, a theme or
   * a mix gets no row at all.
   */
  const addTarget =
    trackEnabled && available("tracklists") ? listForScreen(codes, trackLists) : undefined;

  // Validating a typed ticker needs the universe, which is otherwise fetched
  // only when the search dialog opens. A list on screen is the moment somebody
  // may type into its Add row, so it is asked for then — once per page.
  useSymbolIndex(addTarget !== undefined);

  /**
   * ⋯ › Paste from clipboard, Import symbols…, Export symbols….
   * `docs/features/tracklists.md`, *Import, paste, export*. Import is into the
   * list on screen only; Export is any screen.
   */
  // `list` is set when the command search's Paste symbols… picked a list other
  // than the one on screen; absent, the import files into `addTarget` as before.
  const [importing, setImporting] = useState<{ text: string; list?: TrackList } | null>(null);
  const [exporting, setExporting] = useState(false);
  const [gridExporter, setGridExporter] = useState<ScreenerExporter | null>(null);

  /**
   * The phone's rows are not an AG Grid, so there is no grid to hand one over.
   * The detail frame stands in: its rows as they arrived, and the screen's
   * shown columns as raw values — the phone's own search and conditions are not
   * applied, which the dialog's count makes visible.
   */
  const frameExporter = useMemo<ScreenerExporter | null>(() => {
    const detail = activeFrames.find((frame) => frame.name === "detail");
    if (!detail) return null;
    const names = new Set(detail.fields.map((field) => field.name));
    const shown = (effectiveConfig.columns ?? []).filter((c) => !c.hide && names.has(c.field));
    return {
      rows: () => detail.rows,
      csv: () =>
        [
          shown.map((c) => csvCell(c.header ?? c.field)).join(","),
          ...detail.rows.map((row) => shown.map((c) => csvCell(row[c.field])).join(",")),
        ].join("\n"),
      rowOf: (id) => {
        const row = detail.rows.find((r) => r["symbol"] === id);
        return row && shown.map((c) => ({ field: c.field, header: c.header ?? c.field, value: row[c.field] }));
      },
    };
  }, [activeFrames, effectiveConfig.columns]);
  const exporter = renderRows ? frameExporter : gridExporter;

  const pasteFromClipboard = useCallback((list?: TrackList) => {
    // Firefox and Safari may refuse a read, or ask; the dialog opens either way,
    // empty, and a Ctrl+V into it needs no permission at all.
    void (navigator.clipboard?.readText?.() ?? Promise.reject(new Error("no clipboard")))
      .then((text) => setImporting({ text, list }))
      .catch(() => setImporting({ text: "", list }));
  }, []);

  /**
   * Export and Paste for the command search (`lib/screener-commands.ts`): the
   * same two dialogs, a second caller. Gated exactly as the ⋯ menu gates them —
   * export where there are rows, paste only with the track-list feature — and
   * paste may name any of the user's lists, which the command search asks for
   * when none is on screen.
   */
  const tracksOffered = trackEnabled && available("tracklists");
  /** Bumped by the command search's "Open the group list" — see `ScreenerBar`. */
  const [pickerNonce, setPickerNonce] = useState(0);
  /**
   * Select groups — the command search and the agent. The bar's own edit,
   * `GroupSelection`, made in one step: the working set (Multi Select) becomes
   * these groups, or gains them with `add`, combined as asked. A market filter
   * that would hide one of them is widened to show it.
   */
  const selectGroups = useCallback(
    (groups: readonly SymbolGroup[], combine: CombineMode, add: boolean) =>
      setSelection((was) => {
        const base = asWorkingSet(was);
        const kept = add ? base.picked : [];
        const picked = [
          ...kept,
          ...groups
            .filter((g) => !kept.some((p) => p.groupType === g.groupType && p.code === g.code))
            .map((g) => ({ groupType: g.groupType, code: g.code, on: true })),
        ].map((p) => (groups.some((g) => g.groupType === p.groupType && g.code === p.code) ? { ...p, on: true } : p));
        const markets =
          base.markets.length === 0
            ? base.markets
            : [...new Set([...base.markets, ...groups.map((g) => g.market).filter((m): m is string => !!m)])];
        return { ...base, view: "multi", picked, combine, markets };
      }),
    [],
  );
  useEffect(
    () =>
      publishScreenerCommands({
        exportSymbols: exporter ? () => setExporting(true) : undefined,
        listOnScreen: addTarget,
        pasteInto: tracksOffered ? pasteFromClipboard : undefined,
        rowOf: exporter
          ? (symbol) => {
              const columns = exporter.rowOf(symbol);
              return columns && { screen: screenName, columns };
            }
          : undefined,
        groups: allGroups.length > 0 ? () => allGroups : undefined,
        selectGroups: allGroups.length > 0 ? selectGroups : undefined,
        // The desktop bar's picker; the phone's touch bars have none to open.
        openGroupPicker: !renderRows && allGroups.length > 0 ? () => setPickerNonce((n) => n + 1) : undefined,
      }),
    [exporter, addTarget, tracksOffered, pasteFromClipboard, screenName, allGroups, selectGroups, renderRows],
  );

  /**
   * Σ — the rows shown, charted as one index: TopDown's `~TDe.<hash>` /
   * `~TDc.<hash>`, so the bars are `group_index.index_bars`, the same build as
   * a sector row. Members as of the click; the weighting is this browser's.
   */
  const [indexWeighting, setIndexWeighting] = useState(screenIndexWeighting);
  const pickIndexWeighting = useCallback((weighting: "equal" | "cap") => {
    setScreenIndexWeighting(weighting);
    setIndexWeighting(weighting);
  }, []);
  const chartIndex = useCallback(() => {
    if (!exporter || !onSelectSymbol) return;
    const symbol = registerScreenIndex(
      exporter.rows().map((row) => String(row["symbol"] ?? "")),
      screenName ?? "Screen",
      indexWeighting,
    );
    if (symbol) onSelectSymbol(symbol);
  }, [exporter, onSelectSymbol, screenName, indexWeighting]);

  const listTools: ListTools = {
    onPaste: addTarget ? () => pasteFromClipboard() : undefined,
    onImport: addTarget ? () => setImporting({ text: "" }) : undefined,
    onExport: exporter ? () => setExporting(true) : undefined,
    // Every row on screen, after the filter and the search: the exporter's
    // rows, so it is exactly what Export symbols… would write.
    onNewList:
      exporter && tracksOffered
        ? () => openNewList(exporter.rows().map((row) => String(row["symbol"] ?? "")))
        : undefined,
  };

  // The bar also carries Hide (⋯), and that is the only way to hide the
  // screener — so it renders whenever there is something to hide or a screen
  // to switch, not only when the symbol catalog loaded. Gating it on the
  // catalog would make an unhideable panel out of a symbol service that is down.
  const bar = ((symbolsConfigured && types.length > 0) || onHide || screens.length > 0) && (
    <ScreenerBar
      screens={screens}
      current={currentScreen}
      recent={recentList}
      onPickScreen={pickScreen}
      onNewScreen={onNewScreen}
      onEditScreen={onEditScreen}
      types={types}
      groupsByType={groupsByType}
      selection={selection}
      onSelectionChange={setSelection}
      openPickerNonce={pickerNonce}
      // An empty list is the normal first state: you make it, then fill it.
      onNewList={trackEnabled ? () => openNewList() : undefined}
      settings={
        recsInForce ? (
          <RecsBar
            inline
            recs={recsInForce}
            onChange={onConfigChange ? (recs) => handleConfigChange({ recs }) : undefined}
          />
        ) : stratCode ? (
          <StrategyStepper
            month={stratMonth ?? stratWhy?.month.slice(0, 7) ?? null}
            status={stratWhy?.status ?? null}
            canBack={stratNewest !== null}
            canForward={stratMonth !== undefined}
            onStep={stepStrat}
          />
        ) : (
          extraSettings
        )
      }
      count={barCount}
      hasFilters={hasFilters}
      filtersOn={filtersEnabled}
      onFiltersToggle={() => setFiltersEnabled((on) => !on)}
      direction={config.direction}
      directionTitle={directionTitle(
        config.direction,
        directionField(config.sort),
        panelMeta.find((m) => m.field === directionField(config.sort))?.label ?? "",
      )}
      // A Strat screen is the strategy's rows, all of them — nothing to split.
      onDirectionCycle={
        onConfigChange && !stratCode
          ? () => handleConfigChange({ direction: nextDirection(config.direction) })
          : undefined
      }
      columnsOpen={columnsOpen}
      // Inside a TopDown the grid is the base screen, read-only here; the
      // columns the TopDown owns are its levels', in its own editor.
      // ▥ on a screen opens the editor's Columns tab; the menu's ✎ Edit this
      // screen opens the builder — one callback, told which it was.
      onColumnsToggle={screenHost && onEditScreen ? () => onEditScreen("columns") : () => setColumnsOpen((open) => !open)}
      onRefresh={handleRefresh}
      refreshing={refreshing}
      onIndex={exporter && onSelectSymbol ? chartIndex : undefined}
      leaders={leadersOffered ? { on: leadersOn, onToggle: () => setLeadersOn((on) => !on) } : undefined}
      indexWeighting={indexWeighting}
      onIndexWeighting={pickIndexWeighting}
      onHide={onHide}
      moreSection={moreSection}
      listTools={listTools}
      dockSide={dockSide}
    />
  );

  /**
   * What the column picker offers — the detail frame's fields, merged with the
   * columns the user authored.
   *
   * Built here rather than taken from the panel because the button that opens
   * it now lives in the bar, and the panel's own copy describes whichever tab
   * is showing. This one is always the detail schema, which is the only one
   * whose column view is written back.
   */
  const universe = useMemo(
    () =>
      buildUniverse({
        fields: activeFrames.find((f) => f.name === "detail")?.fields ?? [],
        meta: panelMeta,
        columns: config.columns,
        // What the server said it served. Only ever narrows what is drawn as
        // waiting — a computed column nobody acknowledged is nobody's to send.
        sources: readColumnSources(activeFrames),
      }).filter((column) => pd || !isPdField(column.field)),
    [activeFrames, panelMeta, config.columns, pd],
  );

  // The bar wraps every state, not just the happy one. If it only rendered
  // alongside a loaded grid, a user looking at "No group picked" would have no
  // way to pick one — the bar *is* the way out of every empty state here.
  const body = () => {
    if (error && activeFrames.length === 0) {
      return <Notice title="Screener unavailable" detail={plainText(error)} />;
    }
    /**
     * **The group catalog is missing, so nothing here can work.**
     *
     * Said before the empty line, because it outranks it: with no groups there
     * are no chips to pick from and the seed cannot run, so "No group picked"
     * would be true and useless — it reads as a choice the user can make, and
     * there is nothing to choose from. Naming the unreachable service is the
     * only sentence that points at the actual fault. An earlier version of this
     * state printed a dead screen's name instead and cost an evening.
     *
     * `symbolsConfigured` distinguishes this from a deliberately unconfigured
     * build, where an empty catalog is the expected quiet state.
     */
    if (symbolsConfigured && types.length === 0 && allGroups.length === 0) {
      return (
        <Notice
          title="Groups not loading"
          detail="Cannot reach the symbol service. On another device, check that it allows connections from this network."
        />
      );
    }
    if (activeFrames.length === 0) {
      // Named by the codes when there are any. With none there is nothing to
      // name and nothing loading — the sentence stops, because anything after
      // it would be describing a screen this view is not going to fetch.
      const what = codes.length > 0 ? `Loading ${codes.join(", ")}…` : "No group picked";
      return <Notice title="Screener" detail={what} />;
    }
    return panel();
  };

  // ── track lists, the derived half ──────────────────────────────────────────
  //
  // The star column, its popover, and the digit keys. Nothing stateful is held
  // here — `lib/track-lists.ts` is a module rather than a context, because the
  // symbol search dialog needs the same answers and a second copy of them is a
  // second copy that can disagree.

  /**
   * Every selected symbol, for the digit keys.
   *
   * A ref rather than state: the key handler is the only reader and it reads at
   * the moment of the keystroke, so putting this in state would re-render the
   * whole screener on every ctrl-click to publish something nothing draws.
   */
  const selectedSymbols = useRef<readonly string[]>([]);
  const handleSelectedSymbols = useCallback((symbols: readonly string[]) => {
    selectedSymbols.current = symbols;
  }, []);

  /**
   * What each symbol was trading at, from the rows already on screen.
   *
   * **The grid's own number, not a second lookup.** "Close at the time of add"
   * means the price the user was looking at when they clicked; asking the server
   * for its latest would be a different number during a session, and one nobody
   * saw. A symbol with no row here — added from the symbol search — sends
   * nothing, and the store keeps null.
   */
  const closesFor = useCallback(
    (symbols: readonly string[]): Record<string, number> => {
      const detail = activeFrames.find((frame) => frame.name === "detail");
      if (!detail) return {};
      const names = new Set(detail.fields.map((field) => field.name));
      const priceField = ["close", "last", "price"].find((name) => names.has(name));
      if (!priceField) return {};
      const wanted = new Set(symbols.map((symbol) => symbol.toUpperCase()));
      const out: Record<string, number> = {};
      for (const row of detail.rows) {
        const symbol = String(row["symbol"] ?? "").toUpperCase();
        if (!wanted.has(symbol)) continue;
        const value = row[priceField];
        if (typeof value === "number" && Number.isFinite(value) && value > 0) {
          out[symbol] = value;
        }
      }
      return out;
    },
    [activeFrames],
  );

  const colourOf = useCallback(
    (list: TrackList) => resolveToken(colourToken(list.colour)) ?? "var(--qf-accent)",
    [resolveToken],
  );

  /**
   * The binding handed to the grid.
   *
   * Rebuilt on every render on purpose — `ScreenerGrid` holds it in a ref and
   * depends only on `version`, so a new object here costs nothing and a stale
   * closure over the lists would cost a star that never fills.
   */
  const track: TrackListBinding | undefined = trackEnabled
    ? {
        marks: (symbol: string): readonly TrackMark[] =>
          listsFor(symbol).map((list) => ({ id: list.id, colour: colourOf(list) })),
        onOpen: (symbols, anchor, alt) => {
          const target = defaultList();
          // Alt-click is the one-decision path: straight into the default list,
          // no menu. With no list to be default there is nothing to drop into,
          // so it falls through to the menu, whose only row is "New list…".
          if (alt && target) {
            void toggleSymbols(target.id, symbols, closesFor(symbols)).catch(() => {});
            return;
          }
          setListMenu({ symbols: [...symbols], anchor });
        },
        version: trackVersion,
      }
    : undefined;


  /**
   * The Add row's binding. Rebuilt every render for `track`'s reason: the grid
   * holds it in a ref, and a stale closure would file into the list that was on
   * screen a click ago.
   */
  const addRow: AddRowBinding | undefined = addTarget
    ? {
        suggest: (text, caret) => addRowSuggestions(symbolIndex(), text, caret),
        onAdd: async (text) =>
          addFromRow(text, {
            rows: symbolIndex() ?? (await loadSymbolIndex()),
            // The store as it is *now*, not as this render saw it: two quick
            // Enters must not both pass the "already in" check.
            list: trackListsSnapshot().find((list) => list.id === addTarget.id) ?? addTarget,
            add: addSymbols,
            // **Asked for, so it appears now.** The push loop re-reads the
            // list's roster on every rebuild (`_push_loop`, every 60s), so the
            // row would arrive on its own — a minute after Enter, which reads as
            // the add having failed. A resubscribe builds and sends at once; on
            // the single-GET path it is the only way the row arrives at all.
            // The same nonce the Refresh button uses.
            refresh: handleRefresh,
          }),
      }
    : undefined;

  /**
   * `1`–`9` toggle the selected rows in that list.
   *
   * Bound to the screener's own subtree rather than the window, so a digit typed
   * into the filter bar or the DSL editor is a digit. Letters are deliberately
   * not bound: `lib/type-to-search.ts` takes every plain letter in the app to
   * seed the symbol search, and stealing them here would mean that clicking a
   * row and typing `A` filed it instead of charting Apple.
   */
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (!trackEnabled) return;
      // Somebody nearer the key already took it. The Add row's Symbol cell does
      // exactly that with a digit — it starts its editor with it, AG Grid marks
      // the event handled — and the digit is the first character of a ticker
      // then, not "file the selected rows into list 3".
      if (event.defaultPrevented) return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (!/^[1-9]$/.test(event.key)) return;
      const target = event.target as HTMLElement | null;
      // The same guard `type-to-search.ts` applies, and for the same reason: a
      // digit typed into a field is a digit, and eating it is a bug the user
      // discovers two characters later.
      if (
        target &&
        (["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName) ||
          target.closest('[contenteditable]:not([contenteditable="false"])'))
      ) {
        return;
      }
      const symbols = selectedSymbols.current;
      if (symbols.length === 0) return;
      const list = listForKey(event.key);
      if (!list) return;
      event.preventDefault();
      event.stopPropagation();
      void toggleSymbols(list.id, symbols, closesFor(symbols)).catch(() => {});
    },
    [trackEnabled, closesFor],
  );

  const panel = () =>
    renderRows ? (
      renderRows({
        frames: activeFrames,
        config: effectiveConfig,
        meta: panelMeta,
        onSelectSymbol,
        query: touchQuery,
        onRowCounts: handleRowCounts,
      })
    ) : (
    <ScreenerPanel
      frames={gridFrames}
      // Merged, not raw: a computed column exists only in the config, so the
      // panel's filter bar and column picker would not know it exists — the
      // same absence that hid it from the builder.
      meta={panelMeta}
      config={gridConfig}
      mintId={mintId}
      onSavedFiltersChange={onSavedFiltersChange}
      // ★ and group order only where an edit can land: no screen (the panel's
      // own library) or one of yours. A group's screen, seen by a member, is
      // read-only here — its library is the author's.
      // An authored screen is the author's library, so it is editable too.
      canEditLibrary={
        !config.screenRef ||
        authored ||
        screens.some((s) => s.key === config.screenRef && s.ownerKind === "user")
      }
      resolveToken={resolveToken}
      locale={screenLocale}
      // The `--ag-*` overrides do the coloring; the class still decides the icon
      // set and shadows, so a light theme needs the light one underneath.
      themeClass={agThemeClass(theme.mode)}
      onConfigChange={handleConfigChange}
      status={statusLine}
      // Refresh and Columns are the bar's now. The island keeps both for hosts
      // that render it standalone; here they would be a second copy of a
      // control the user already has one of, three centimetres away.
      hideColumns
      onRowCounts={handleRowCounts}
      onExporter={setGridExporter}
      onSelectSymbol={onSelectSymbol}
      onHoverSymbol={narrow ? undefined : spark.onHoverSymbol}
      onSelectedSymbols={handleSelectedSymbols}
      track={track}
      onNewsOpen={openNews}
      addRow={addRow}
      // The Symbol search is cleared by a new universe or a new screen: a
      // filter typed against one must not silently thin out the next.
      searchScope={`${codesKey}|${config.screenRef ?? ""}`}
      liveWindow={live}
      categoryOf={categoryOf}
      autoHeight={stratCode !== null}
    />
  );

  const stratStrip = stratCode && (
    <StrategyHistoryStrip
      history={stratHistory}
      why={stratWhy}
      month={stratMonth ?? stratWhy?.month ?? null}
      onPick={pickStrat}
      capped={renderRows !== undefined}
    />
  );

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        // The positioning context for the column picker — see the header.
        position: "relative",
      }}
      // The digit keys, scoped to the screener's subtree. `onKeyDown` bubbles
      // from the grid, so this hears a keypress only when focus is already
      // inside — which is what keeps `3` typed at a chart from filing a row.
      onKeyDown={handleKeyDown}
    >
      {/* On a touch screen the bar is rearranged — applied at the top, controls
          at the bottom (`ScreenerTouchBars`); the desktop keeps its two rows. */}
      {!renderRows && bar}
      {/* The desktop bar carries the Recs settings itself; the touch shell
          has no bar, so they keep a row of their own there. `draft` is only
          ever read for an authored screen, so a member has none. */}
      {renderRows && recsInForce && (
        <RecsBar
          recs={recsInForce}
          onChange={onConfigChange ? (recs) => handleConfigChange({ recs }) : undefined}
        />
      )}
      {authored && draftUnread === config.screenRef && (
        <DraftUnreadBar
          busy={draftRetrying}
          onRetry={() => {
            const key = config.screenRef;
            if (!key) return;
            setDraftRetrying(true);
            void applyScreen(key).finally(() => setDraftRetrying(false));
          }}
        />
      )}
      {authored && draft && draftUnread !== config.screenRef && (
        <DraftBar
          meta={draft}
          actorKey={me?.actorKey}
          version={authoredScreen?.version}
          busy={draftBusy}
          error={draftError}
          onDiscard={() => void handleDiscard()}
          onPublish={() => void handlePublish()}
        />
      )}
      {renderRows ? (
        <div style={{ flex: "1 1 auto", minHeight: 0 }}>
          <ScreenerTouchBars
            types={types}
            groupsByType={groupsByType}
            selection={selection}
            onSelectionChange={setSelection}
            total={activeFrames.length === 0 ? null : totalRows}
            matched={matchedRows}
            filters={config.savedFilters ?? []}
            categoryOrder={config.savedFilterCategoryOrder}
            selectedIds={selectedFilterIds(config)}
            onFilterIds={(ids) => handleConfigChange(applySelection(config, ids))}
            filtersOn={filtersEnabled}
            onFiltersToggle={() => setFiltersEnabled((on) => !on)}
            onRefresh={handleRefresh}
            refreshing={refreshing}
            onColumns={onConfigChange ? () => setColumnsOpen(true) : undefined}
            listTools={listTools}
            query={touchQuery}
            onQuery={setTouchQuery}
            rail={landscape}
            tablet={tablet}
          >
            {body()}
          </ScreenerTouchBars>
        </div>
      ) : stratCode ? (
        // A Strat list is a handful of rows: the grid takes their height and the
        // strip sits right under it, one area scrolling only when it must
        // (owner, 2026-10-27).
        <div style={{ flex: "1 1 auto", minHeight: 0, overflowY: "auto" }}>
          {frameGrid ? frameGrid(body()) : body()}
          {stratStrip}
        </div>
      ) : (
        <div style={{ flex: "1 1 auto", minHeight: 0 }}>
          {frameGrid ? (
            frameGrid(body())
          ) : leadersActive ? (
            <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
              <LeadersPanel
                settings={leaders}
                onChange={setLeaders}
                view={leadersView}
                resolvedDate={leadersAnswer?.resolvedDate ?? null}
                priorDate={leadersAnswer?.priorDate ?? null}
                universe={leaderSymbols.length}
                loading={leadersLoading}
                error={leadersError}
                today={new Date().toLocaleDateString("en-CA")}
              />
              <div style={{ flex: "1 1 auto", minHeight: 0 }}>{body()}</div>
            </div>
          ) : (
            body()
          )}
        </div>
      )}
      {renderRows && stratStrip}
      {columnsOpen && onConfigChange && (
        <ColumnPicker
          universe={universe}
          columns={config.columns ?? []}
          onChange={(columns) => onConfigChange({ columns })}
          onClose={() => setColumnsOpen(false)}
        />
      )}
      {newsList && (
        <NewsListPopover
          symbol={newsList.symbol}
          window={newsList.window}
          anchor={newsList.anchor}
          onClose={closeNews}
        />
      )}
      {listMenu && available("tracklists") && (
        <TrackListMenu
          anchor={listMenu.anchor}
          symbols={listMenu.symbols}
          lists={trackLists}
          colourOf={colourOf}
          // **The menu stays open on a tick** — ticking is what you came to do,
          // and two lists then cost one visit. Nothing is lost by closing it
          // either, because the write has already gone out.
          onToggle={(list) => {
            void toggleSymbols(list.id, listMenu.symbols, closesFor(listMenu.symbols)).catch(
              () => {},
            );
          }}
          onCreate={() => {
            openNewList(listMenu.symbols);
            setListMenu(null);
          }}
          onRemoveAll={() => {
            void removeFromAll(listMenu.symbols).catch(() => {});
            setListMenu(null);
          }}
          onClose={() => setListMenu(null)}
        />
      )}
      {newList && (
        <NewListDialog
          lists={trackLists}
          resolve={resolveToken}
          seedCount={newList.symbols.length}
          overCount={newList.over}
          error={newListError}
          busy={newListBusy}
          onCreate={(spec) => {
            setNewListBusy(true);
            setNewListError(null);
            // In chunks: the server takes 500 symbols a call, and a whole
            // screen or a Ctrl+A selection is often more.
            void createListInChunks(
              newList.symbols,
              closesFor(newList.symbols),
              (symbols, closes) =>
                createList({
                  id: mintId(),
                  name: spec.name,
                  hotKey: spec.hotKey,
                  colour: spec.colour,
                  isDefault: spec.isDefault,
                  symbols,
                  closes,
                }),
              addSymbols,
            )
              .then(() => setNewList(null))
              // Shown verbatim rather than swallowed: "you already have a list
              // called AI" is the caller being told a rule, and the dialog is
              // where it can be acted on.
              .catch((err: unknown) =>
                setNewListError(err instanceof Error ? err.message : String(err)),
              )
              .finally(() => setNewListBusy(false));
          }}
          onClose={() => setNewList(null)}
        />
      )}
      {importing && (importing.list ?? addTarget) && (
        <TrackListImportDialog
          // The store as it is now, so a second import sees the first's symbols as already in.
          list={(() => {
            const into = (importing.list ?? addTarget)!;
            return trackLists.find((list) => list.id === into.id) ?? into;
          })()}
          rows={symbolIndex()}
          initialText={importing.text}
          add={addSymbols}
          onAdded={handleRefresh}
          onClose={() => setImporting(null)}
        />
      )}
      {exporting && exporter && (
        <ScreenerExportDialog
          exporter={exporter}
          title={addTarget?.name ?? (codes.length > 0 ? codes.join(" + ") : "Screen")}
          list={addTarget ? (trackLists.find((list) => list.id === addTarget.id) ?? addTarget) : undefined}
          onClose={() => setExporting(false)}
        />
      )}
      {spark.card}
    </div>
  );
}

function Notice({ title, detail }: { title: string; detail: string }) {
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "0.35rem",
        padding: "1rem",
        textAlign: "center",
        background: "var(--qf-surface-alt)",
        color: "var(--qf-text-secondary)",
      }}
    >
      <span style={{ fontSize: "0.75rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>
        {title}
      </span>
      <span style={{ fontSize: "0.8rem" }}>{detail}</span>
    </div>
  );
}
